import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { frontloadConnect } from 'react-frontload';
import { isEmpty, isNil, maxBy, minBy } from 'lodash';
import { fetchServiceAngeboteIfNeeded, setServiceAngeboteFilterValues } from '../../../modules/actions/standort/service/serviceAngebote';
import BreadCrumbSelector from '../../components/BreadCrumb/BreadCrumbSelector';
import InlineSpinner from '../../components/Spinner/InlineSpinner';
import ReduxSuspense from '../../components/ReduxSuspense/ReduxSuspense';
import ServiceList from '../../components/service-list';
import ProductListFooter from '../../components/product-list/footer';
import FilterLayout from '../../components/Filter/FilterLayout';
import CheckboxGroup from '../../components/form-elements/CheckboxGroup';
import PriceRangeFilter from '../../components/Filter/PriceRangeFilter/PriceRangeFilter';
import ServiceRequest from '../ServiceRequest/ServiceRequest';
import ServiceLandingpage from '../ServiceLandingpage/ServiceLandingpage';
import { serviceAngeboteResponseSelector } from '../../../modules/selectors/standort/service/serviceAngebote';
import { markenStandortSelector } from '../../../modules/selectors/standort/markenStandort';
import { categoriesSelector } from '../../../modules/selectors/standort/categories';
import { InternalLink } from '../../components/form-elements/Link';
import OptionsSidebar from '../../components/OptionsSidebar/OptionsSidebar';
import LegacyShopWrapper from '../../LegacyShopWrapper';

const defaultFilterValues = {
    serviceUrls: [],
    automodell: [],
    automarke: [],
    preisspanne: {
        minPrice: 0,
        maxPrice: 0,
    },
};

const FilterBoxen = ({ serviceUrl, serviceTitles, serviceAngebote, filterValues }) => {
    const dispatch = useDispatch();
    const options = useMemo(() => {
        let automodellOptions;
        if (isEmpty(serviceAngebote.automarke)) {
            automodellOptions = [...new Set(serviceAngebote.map(product => product.autoModell).filter(x => !isNil(x)))];
        } else {
            automodellOptions = [
                ...new Set(
                    serviceAngebote
                        .filter(product => serviceAngebote.automarke.indexOf(product.hauptFabrikat) > -1)
                        .map(product => product.autoModell)
                        .filter(x => !isNil(x))
                ),
            ];
        }

        const automarkeOptions = [...new Set(serviceAngebote.map(product => product.hauptFabrikat).filter(x => !isNil(x)))];

        const preisspanne = {
            // TODO: filter services without price server-side.
            minPrice: Math.floor(minBy(serviceAngebote, 'preis.komplettPreis')?.preis?.komplettPreis),
            maxPrice: Math.ceil(maxBy(serviceAngebote, 'preis.komplettPreis')?.preis?.komplettPreis),
        };

        return {
            automodell: automodellOptions.sort(),
            automarke: automarkeOptions.sort(),
            preisspanne,
        };
    }, [serviceAngebote]);

    const onFilterChanged = ({ name, values }) => {
        dispatch(
            setServiceAngeboteFilterValues({
                serviceUrl,
                filterValues: {
                    ...filterValues,
                    [name]: values,
                },
            })
        );
    };
    
    return ( 
        <>
            {!isEmpty(serviceTitles) && (
                <FilterLayout textHeader="Services">
                    <CheckboxGroup
                        name="serviceUrls"
                        options={serviceTitles.map(({ title, url }) => ({ label: title, value: url }))}
                        checkedValues={filterValues.serviceUrls}
                        onChange={onFilterChanged}
                    />
                    <InternalLink modifier="primary" to="/reparatur-wartung/serviceAnfrage" fullWidth>
                        Individuelle Serviceanfrage
                    </InternalLink>
                </FilterLayout>
            )}
            {!isEmpty(options.automarke) && (
                <FilterLayout textHeader="Automarke">
                    <CheckboxGroup
                        name="automarke"
                        options={options.automarke.map(option => ({ label: option, value: option }))}
                        checkedValues={filterValues.automarke}
                        onChange={onFilterChanged}
                    />
                </FilterLayout>
            )}
            {!isEmpty(options.automodell) && (
                <FilterLayout textHeader="Automodell">
                    <CheckboxGroup
                        name="automodell"
                        options={options.automodell.map(option => ({ label: option, value: option }))}
                        checkedValues={filterValues.automodell}
                        onChange={onFilterChanged}
                    />
                </FilterLayout>
            )}
            {!isEmpty(options.preisspanne) && options.preisspanne.minPrice < options.preisspanne.maxPrice && (
                <FilterLayout textHeader="Preis">
                    <PriceRangeFilter
                        name="preisspanne"
                        currentMinPrice={
                            filterValues.preisspanne?.minPrice ? filterValues.preisspanne.minPrice : options.preisspanne.minPrice
                        }
                        currentMaxPrice={
                            filterValues.preisspanne?.maxPrice ? filterValues.preisspanne.maxPrice : options.preisspanne.maxPrice
                        }
                        minPrice={options.preisspanne.minPrice}
                        maxPrice={options.preisspanne.maxPrice}
                        onFilterChanged={values => onFilterChanged({ name: 'preisspanne', values })}
                    />
                </FilterLayout>
            )}
        </>
    );
};

const InnerLoadServices = ({ serviceUrl, serviceAngeboteResponse }) => {
    return (
        <ReduxSuspense response={serviceAngeboteResponse} fallback={<InlineSpinner />}>
            {({ serviceAngebote, serviceTitle, filterValues = defaultFilterValues }) => {
                if (isEmpty(serviceAngebote)) return <ServiceRequest />;
                return (
                    <section className="uk-section uk-section-default">
                        <div className="uk-container">
                            <div className="uk-grid uk-grid-small" data-uk-grid="">
                                <OptionsSidebar>
                                    <FilterBoxen
                                        serviceUrl={serviceUrl}
                                        serviceTitles={serviceTitle}
                                        serviceAngebote={serviceAngebote}
                                        filterValues={filterValues}
                                    />
                                </OptionsSidebar>
                                <div className="uk-width-expand">
                                    <ServiceList serviceAngebote={serviceAngebote} filterValues={filterValues} />
                                    <ProductListFooter />
                                </div>
                            </div>
                        </div>
                    </section>
                );
            }}
        </ReduxSuspense>
    );
};

const frontload = async ({ serviceUrl, fetchServiceAngeboteIfNeeded }) => {
    await Promise.all([fetchServiceAngeboteIfNeeded({ serviceUrl })]);
};
const mapDispatchToProps = { fetchServiceAngeboteIfNeeded };
const mapStateToProps = (state, { serviceUrl }) => {
    return {
        serviceUrl,
        serviceAngeboteResponse: serviceAngeboteResponseSelector(state, { serviceUrl }),
    };
};

const LoadServices = connect(mapStateToProps, mapDispatchToProps)(frontloadConnect(frontload, { onUpdate: true })(InnerLoadServices));

function Services() {
    const { serviceUrl } = useParams();
    const markenStandort = useSelector(markenStandortSelector);
    const categories = useSelector(categoriesSelector);

    return (
        <>
            <BreadCrumbSelector additionalCrumbs={[{ label: serviceUrl.replace(/ue/g, 'ü').replace(/oe/g, 'ö').replace(/ae/g, 'ä') }]} />
            {markenStandort.isTechno ? (
                <>
                    {serviceUrl === 'serviceAnfrage' ? (
                        <ServiceRequest />
                    ) : (
                        <LegacyShopWrapper>
                            <ServiceLandingpage
                                serviceUrl={serviceUrl}
                                standortUrl={markenStandort.url}
                                markenStandort={markenStandort}
                                categories={categories}
                            />
                        </LegacyShopWrapper>
                    )}
                </>
            ) : (
                <LoadServices serviceUrl={serviceUrl} />
            )}
        </>
    );
}

export default Services;
